import { create } from "zustand";
import { devtools } from "zustand/middleware";
import {
  IGetManyNotificationBarQueryReq,
  IGetManyNotificationBarRes,
} from "@evennt-tech/api-v2-external";
import {
  INotificationBar,
  INotificationBaseRelatedToShow,
  NotificationType,
} from "@evennt-tech/api-v2-core";
import { apiV2 } from "api/firebase";
import { registerStore } from "store/utils/reset-store.utils";

type PaginationMeta = {
  page: number;
  limit: number;
  total: number;
};

type NotificationBarState = {
  data: Record<string, INotificationBar[]>;
  pagination: Record<string, PaginationMeta>;
  isLoading: boolean;
};

type LoadMoreParams = {
  showId: string;
  limit: number;
};

type FetchParams = Omit<IGetManyNotificationBarQueryReq, "showId"> & {
  showId: string;
  replace?: boolean;
};

type NotificationBarActions = {
  fetch: (req: FetchParams) => Promise<void>;
  reload: (showId: string) => void;
  autoCatch: (showId: string) => void;
  loadMore: (data: LoadMoreParams) => void;
};

export const useNotificationBar = create<
  NotificationBarState & NotificationBarActions
>()(
  devtools((set, get) => ({
    data: {},
    pagination: {},
    isLoading: false,

    fetch: async ({ showId, page, limit, replace = false }) => {
      const { data, isLoading } = get();

      const alreadyFetched = !replace && page === 1 && data[showId]?.length;
      if (alreadyFetched || isLoading) return;

      set({ isLoading: true });

      try {
        const res: IGetManyNotificationBarRes = await apiV2.notificationBar.getMany(
          { showId, page, limit }
        );

        set((state) => {
          const prevTimelines = replace ? [] : state.data[showId] || [];
          const newItems = res.items;

          return {
            data: {
              ...state.data,
              [showId]: [...prevTimelines, ...newItems],
            },
            pagination: {
              ...state.pagination,
              [showId]: {
                page: res.meta.currentPage,
                limit: res.meta.itemsPerPage,
                total: res.meta.totalItems ?? 0,
              },
            },
          };
        });
      } catch (err) {
        console.error("Error fetching timelines:", err);
      } finally {
        set({ isLoading: false });
      }
    },

    // The current items stay until the first page is back: emptying the list
    // first showed as the notifications vanishing and reappearing.
    reload: async (showId) => {
      const { fetch } = get();

      await fetch({ showId, page: 1, limit: 15, replace: true });
    },

    autoCatch: (showId) => {
      const { reload } = get();

      const keys = [NotificationType.UPDATED_NOTIFICATION_BAR];

      for (const key of keys) {
        apiV2.notification.subscribe<INotificationBaseRelatedToShow>({
          key,
          showId,
          listenerId: "notification_bar_store",
          callback: ({ showId }) => {
            reload(showId);
          },
        });
      }
    },

    loadMore: ({ showId, limit }) => {
      const { pagination, data, isLoading, fetch } = get();
      const currentPage = pagination[showId]?.page || 0;
      const total = pagination[showId]?.total || 0;
      const currentLength = data[showId]?.length || 0;

      if (isLoading || currentLength >= total) return;

      const nextPage = currentPage + 1;
      fetch({ showId, page: nextPage, limit });
    },
  }))
);

registerStore(useNotificationBar);
